import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { uploadAssetFile, sha256HexOfFile } from '../lib/uploadFile';
import { getStoredToken } from './../lib/tokenStore';

const WORKER_ORIGIN = 'https://skey-worker.sonhangtravel.workers.dev';

/**
 * Server-side AI background removal via the Worker proxy. Sends the PNG
 * tile to `POST /admin/v1/bg-remove`; the Worker forwards to remove.bg
 * and returns the cut-out PNG.
 */
async function removeBgViaWorker(blob: Blob): Promise<Blob> {
  const token = getStoredToken();
  if (!token) throw new Error('Chưa đăng nhập admin');
  const res = await fetch(`${WORKER_ORIGIN}/admin/v1/bg-remove`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': blob.type || 'image/png',
    },
    body: blob,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Bg removal thất bại (${res.status}): ${text.slice(0, 200)}`);
  }
  return await res.blob();
}

/**
 * Multi-tile sticker cropper (v3).
 *
 * Capabilities:
 *   1. Drop one source image, choose any rows × cols (1..10).
 *   2. Outer crop frame draggable + resizable (8 handles + body drag).
 *   3. Each interior grid line has its own drag handle so cells aren't
 *      locked to uniform spacing.
 *   4. Background removal modes:
 *        - "Tắt"      → keep the original background.
 *        - "AI"       → run @imgly/background-removal (ONNX U²-Net) per tile;
 *                       state-of-the-art quality, runs locally in the browser.
 *        - "Vẽ tay"   → admin paints a mask on each tile using a brush
 *                       (additive) + eraser (subtractive). Pixels outside
 *                       the painted region get alpha=0.
 *   5. Click any tile thumbnail to skip it (or to open the manual mask
 *      editor when bg mode is "Vẽ tay").
 *   6. Sequential upload via the regular `uploadAssetFile` pipeline
 *      (browser SHA-256 + Worker idempotence).
 */

type BgMode = 'off' | 'manual';

export interface StickerCropperProps {
  packId: string;
  onUploaded: () => void;
  open: boolean;
  onClose: () => void;
}

interface CropState {
  outer: { x: number; y: number; width: number; height: number };
  vSplits: number[];
  hSplits: number[];
}

interface UploadProgress {
  index: number;
  status:
    | 'pending'
    | 'bg-removing'
    | 'hashing'
    | 'uploading'
    | 'done'
    | 'error'
    | 'skipped';
  message?: string;
}

const TILE_PIXEL_SIZE = 512;
const MIN_DIM = 1;
const MAX_DIM = 10;
const DEFAULT_ROWS = 3;
const DEFAULT_COLS = 3;
const MASK_CANVAS_SIZE = 512;

export default function StickerCropper({
  packId,
  onUploaded,
  open,
  onClose,
}: StickerCropperProps): JSX.Element {
  const [imageURL, setImageURL] = useState<string | null>(null);
  const [imageDims, setImageDims] = useState<{ w: number; h: number } | null>(null);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  /** True once the source image has been processed by remove.bg. We
   *  remember the original URL so the user can revert if the cut-out
   *  looks wrong. */
  const [originalImageURL, setOriginalImageURL] = useState<string | null>(null);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [bgRemoveError, setBgRemoveError] = useState<string | null>(null);

  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [cols, setCols] = useState(DEFAULT_COLS);
  const [crop, setCrop] = useState<CropState>(() => buildInitialCrop(DEFAULT_ROWS, DEFAULT_COLS));

  const [bgMode, setBgMode] = useState<BgMode>('off');
  const [skippedTiles, setSkippedTiles] = useState<Set<number>>(new Set());
  /** Per-tile manual mask, keyed by tile index. Lazily filled when admin
   *  opens the manual editor for that tile. ImageData is at MASK_CANVAS_SIZE. */
  const [manualMasks, setManualMasks] = useState<Map<number, ImageData>>(new Map());
  /** When non-null, the manual mask editor modal is open for this tile. */
  const [editingTile, setEditingTile] = useState<number | null>(null);

  const [progress, setProgress] = useState<UploadProgress[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiPreviewProgress, setAiPreviewProgress] = useState<{ index: number; total: number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setImageURL(null);
      setImageDims(null);
      setImageElement(null);
      setOriginalImageURL(null);
      setIsRemovingBg(false);
      setBgRemoveError(null);
      setRows(DEFAULT_ROWS);
      setCols(DEFAULT_COLS);
      setCrop(buildInitialCrop(DEFAULT_ROWS, DEFAULT_COLS));
      setBgMode('off');
      setSkippedTiles(new Set());
      setManualMasks(new Map());
      setEditingTile(null);
      setProgress([]);
      setIsProcessing(false);
      setAiPreviewProgress(null);
    }
  }, [open]);

  useEffect(() => {
    setCrop((prev) => ({
      ...prev,
      vSplits: uniformSplits(cols),
      hSplits: uniformSplits(rows),
    }));
  }, [rows, cols]);

  const handleFile = (file: File): void => {
    if (!file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setImageURL(url);
    setOriginalImageURL(url);
    setBgRemoveError(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageDims({ w: img.naturalWidth, h: img.naturalHeight });
      setImageElement(img);
    };
    img.src = url;
  };

  /**
   * Run remove.bg on the WHOLE source image once. After this, every
   * tile cut from the result already has a transparent background, so
   * we only spend one credit per pack instead of one per sticker.
   *
   * The original image stays in `originalImageURL` so the user can
   * revert if the cut-out is wrong.
   */
  const handleRemoveBgOnSource = async (): Promise<void> => {
    if (!imageURL || isRemovingBg) return;
    setIsRemovingBg(true);
    setBgRemoveError(null);
    try {
      const sourceBlob = await (await fetch(imageURL)).blob();
      // remove.bg rejects GIF; if the source happens to be one we'd need
      // to flatten to PNG first. For a sticker collage that's almost
      // always already PNG/JPG so we just refuse here.
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(sourceBlob.type)) {
        throw new Error('Chỉ hỗ trợ PNG / JPEG / WebP');
      }
      const cleaned = await removeBgViaWorker(sourceBlob);
      const newURL = URL.createObjectURL(cleaned);
      setImageURL(newURL);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImageDims({ w: img.naturalWidth, h: img.naturalHeight });
        setImageElement(img);
      };
      img.src = newURL;
    } catch (err) {
      setBgRemoveError(err instanceof Error ? err.message : 'Xoá nền thất bại');
    } finally {
      setIsRemovingBg(false);
    }
  };

  const handleRevertBg = (): void => {
    if (!originalImageURL || originalImageURL === imageURL) return;
    setImageURL(originalImageURL);
    setBgRemoveError(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageDims({ w: img.naturalWidth, h: img.naturalHeight });
      setImageElement(img);
    };
    img.src = originalImageURL;
  };

  const isCleaned = originalImageURL !== null && originalImageURL !== imageURL;

  const tileCount = rows * cols;

  const tileRects = useMemo(() => {
    const xs = [0, ...crop.vSplits, 1];
    const ys = [0, ...crop.hSplits, 1];
    const rects: { x: number; y: number; width: number; height: number; index: number }[] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cellX = crop.outer.x + xs[c]! * crop.outer.width;
        const cellY = crop.outer.y + ys[r]! * crop.outer.height;
        const cellW = (xs[c + 1]! - xs[c]!) * crop.outer.width;
        const cellH = (ys[r + 1]! - ys[r]!) * crop.outer.height;
        rects.push({ x: cellX, y: cellY, width: cellW, height: cellH, index: r * cols + c });
      }
    }
    return rects;
  }, [crop, rows, cols]);

  // ---- Drag handling -------------------------------------------------

  const overlayRef = useRef<HTMLDivElement>(null);

  type DragTarget =
    | { kind: 'frame'; handle: 'tl' | 'tr' | 'bl' | 'br' | 'l' | 'r' | 't' | 'b' | 'move' }
    | { kind: 'vSplit'; index: number }
    | { kind: 'hSplit'; index: number };

  const dragRef = useRef<{
    target: DragTarget;
    start: CropState;
    pointer: { x: number; y: number };
  } | null>(null);

  const onPointerDown = (target: DragTarget) => (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    overlayRef.current?.setPointerCapture(e.pointerId);
    dragRef.current = {
      target,
      start: { outer: { ...crop.outer }, vSplits: [...crop.vSplits], hSplits: [...crop.hSplits] },
      pointer: { x: e.clientX, y: e.clientY },
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    const overlay = overlayRef.current;
    if (!drag || !overlay) return;
    const rect = overlay.getBoundingClientRect();
    const dxN = (e.clientX - drag.pointer.x) / rect.width;
    const dyN = (e.clientY - drag.pointer.y) / rect.height;

    setCrop(() => {
      const next: CropState = {
        outer: { ...drag.start.outer },
        vSplits: [...drag.start.vSplits],
        hSplits: [...drag.start.hSplits],
      };

      if (drag.target.kind === 'frame') {
        const minSize = 0.05;
        let { x, y, width, height } = next.outer;
        switch (drag.target.handle) {
          case 'move':
            x = clamp(x + dxN, 0, 1 - width);
            y = clamp(y + dyN, 0, 1 - height);
            break;
          case 'tl':
            x = clamp(x + dxN, 0, x + width - minSize);
            y = clamp(y + dyN, 0, y + height - minSize);
            width = drag.start.outer.x + drag.start.outer.width - x;
            height = drag.start.outer.y + drag.start.outer.height - y;
            break;
          case 'tr':
            y = clamp(y + dyN, 0, y + height - minSize);
            height = drag.start.outer.y + drag.start.outer.height - y;
            width = clamp(width + dxN, minSize, 1 - x);
            break;
          case 'bl':
            x = clamp(x + dxN, 0, x + width - minSize);
            width = drag.start.outer.x + drag.start.outer.width - x;
            height = clamp(height + dyN, minSize, 1 - y);
            break;
          case 'br':
            width = clamp(width + dxN, minSize, 1 - x);
            height = clamp(height + dyN, minSize, 1 - y);
            break;
          case 'l':
            x = clamp(x + dxN, 0, x + width - minSize);
            width = drag.start.outer.x + drag.start.outer.width - x;
            break;
          case 'r':
            width = clamp(width + dxN, minSize, 1 - x);
            break;
          case 't':
            y = clamp(y + dyN, 0, y + height - minSize);
            height = drag.start.outer.y + drag.start.outer.height - y;
            break;
          case 'b':
            height = clamp(height + dyN, minSize, 1 - y);
            break;
        }
        next.outer = { x, y, width, height };
      } else if (drag.target.kind === 'vSplit') {
        const localDx = dxN / drag.start.outer.width;
        const before = drag.start.vSplits[drag.target.index - 1] ?? 0;
        const after = drag.start.vSplits[drag.target.index + 1] ?? 1;
        const original = drag.start.vSplits[drag.target.index] ?? 0;
        next.vSplits[drag.target.index] = clamp(original + localDx, before + 0.02, after - 0.02);
      } else {
        const localDy = dyN / drag.start.outer.height;
        const before = drag.start.hSplits[drag.target.index - 1] ?? 0;
        const after = drag.start.hSplits[drag.target.index + 1] ?? 1;
        const original = drag.start.hSplits[drag.target.index] ?? 0;
        next.hSplits[drag.target.index] = clamp(original + localDy, before + 0.02, after - 0.02);
      }
      return next;
    });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    overlayRef.current?.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  };

  // ---- Thumbnails --------------------------------------------------

  const [thumbnails, setThumbnails] = useState<string[]>([]);
  useEffect(() => {
    if (!imageElement || !imageDims) return;
    const cancelToken = { cancelled: false };
    void renderThumbnails(
      imageElement,
      imageDims,
      tileRects,
      bgMode,
      manualMasks,
      cancelToken,
      setAiPreviewProgress,
    ).then((urls) => {
      if (!cancelToken.cancelled) setThumbnails(urls);
    });
    return () => {
      cancelToken.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageElement, imageDims, tileRects, bgMode, manualMasks]);

  // ---- Upload ------------------------------------------------------

  const handleUpload = async (): Promise<void> => {
    if (!imageElement || !imageDims) return;
    setIsProcessing(true);
    setProgress(
      tileRects.map((_, idx) => ({
        index: idx,
        status: skippedTiles.has(idx) ? 'skipped' : 'pending',
      })),
    );

    const canvas = document.createElement('canvas');
    canvas.width = TILE_PIXEL_SIZE;
    canvas.height = TILE_PIXEL_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setIsProcessing(false);
      return;
    }

    for (const tile of tileRects) {
      if (skippedTiles.has(tile.index)) continue;
      try {
        ctx.clearRect(0, 0, TILE_PIXEL_SIZE, TILE_PIXEL_SIZE);
        const { srcX, srcY, srcW, srcH, drawW, drawH, offsetX, offsetY } =
          tilePlacement(tile, imageDims, TILE_PIXEL_SIZE);
        ctx.drawImage(imageElement, srcX, srcY, srcW, srcH, offsetX, offsetY, drawW, drawH);

        let blob: Blob;
        if (bgMode === 'manual') {
          const mask = manualMasks.get(tile.index);
          if (mask) {
            applyManualMask(ctx, TILE_PIXEL_SIZE, mask);
          }
          blob = await new Promise((resolve, reject) => {
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob null'))), 'image/png');
          });
        } else {
          blob = await new Promise((resolve, reject) => {
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob null'))), 'image/png');
          });
        }

        const file = new File([blob], `tile-${tile.index}.png`, { type: 'image/png' });
        setProgress((prev) =>
          prev.map((p) => (p.index === tile.index ? { ...p, status: 'hashing' } : p)),
        );
        const sha256 = await sha256HexOfFile(file);
        setProgress((prev) =>
          prev.map((p) => (p.index === tile.index ? { ...p, status: 'uploading' } : p)),
        );
        const assetId = crypto.randomUUID();
        await uploadAssetFile({ packId, assetId, file, sha256 });

        setProgress((prev) =>
          prev.map((p) => (p.index === tile.index ? { ...p, status: 'done' } : p)),
        );
        onUploaded();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload thất bại';
        setProgress((prev) =>
          prev.map((p) => (p.index === tile.index ? { ...p, status: 'error', message } : p)),
        );
      }
    }

    setIsProcessing(false);
  };

  const enabledCount = tileCount - skippedTiles.size;

  // ---- Render ------------------------------------------------------

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/70" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[94vh] w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-lg border border-slate-200 bg-white p-6 shadow-xl focus:outline-none">
          <header className="flex items-start justify-between">
            <div>
              <Dialog.Title className="text-lg font-semibold text-slate-900">
                Công cụ cắt sticker
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-slate-500">
                Thả 1 ảnh tổng hợp, chọn số hàng / cột, kéo các đường lưới để khít với từng sticker, chọn cách xoá nền, rồi tải lên.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Đóng"
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </Dialog.Close>
          </header>

          {!imageURL ? (
            <DropZone
              onFile={handleFile}
              onPick={() => fileInputRef.current?.click()}
              fileInputRef={fileInputRef}
            />
          ) : (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <NumberStepper label="Hàng" value={rows} onChange={setRows} min={MIN_DIM} max={MAX_DIM} />
                <NumberStepper label="Cột" value={cols} onChange={setCols} min={MIN_DIM} max={MAX_DIM} />
                <button
                  type="button"
                  onClick={() => setCrop({
                    outer: { x: 0.02, y: 0.02, width: 0.96, height: 0.96 },
                    vSplits: uniformSplits(cols),
                    hSplits: uniformSplits(rows),
                  })}
                  className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                >
                  Chia đều
                </button>
                <div className="ml-auto flex items-center gap-2">
                  {isCleaned ? (
                    <>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        ✓ Đã xoá nền
                      </span>
                      <button
                        type="button"
                        onClick={handleRevertBg}
                        className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        Hoàn nguyên
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { void handleRemoveBgOnSource(); }}
                      disabled={isRemovingBg}
                      className="rounded-md bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-purple-300"
                    >
                      {isRemovingBg ? 'Đang xoá nền…' : '✨ Xoá nền (1 credit)'}
                    </button>
                  )}
                </div>
              </div>

              {bgRemoveError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {bgRemoveError}
                </div>
              ) : null}

              {bgMode === 'manual' ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Bấm vào ô bên dưới để mở trình vẽ mask: dùng cọ tô lên vùng cần GIỮ, eraser để bỏ. Phần ngoài sẽ tự động trong suốt.
                </div>
              ) : null}

              <div
                ref={overlayRef}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                className="relative w-full select-none rounded-md bg-slate-900"
                style={{
                  aspectRatio: imageDims ? `${imageDims.w} / ${imageDims.h}` : '1 / 1',
                  touchAction: 'none',
                }}
              >
                <img
                  src={imageURL}
                  alt="Ảnh nguồn"
                  className="absolute inset-0 h-full w-full object-contain"
                  draggable={false}
                />

                <div
                  onPointerDown={onPointerDown({ kind: 'frame', handle: 'move' })}
                  className="absolute"
                  style={{
                    left: `${crop.outer.x * 100}%`,
                    top: `${crop.outer.y * 100}%`,
                    width: `${crop.outer.width * 100}%`,
                    height: `${crop.outer.height * 100}%`,
                    border: '2px solid #6C5CFF',
                    boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)',
                    cursor: 'move',
                    touchAction: 'none',
                  }}
                >
                  {crop.vSplits.map((sx, i) => (
                    <div key={`v-${i}`} className="pointer-events-none absolute inset-y-0" style={{ left: `${sx * 100}%`, width: 1, background: 'rgba(108, 92, 255, 0.7)' }} />
                  ))}
                  {crop.hSplits.map((sy, i) => (
                    <div key={`h-${i}`} className="pointer-events-none absolute inset-x-0" style={{ top: `${sy * 100}%`, height: 1, background: 'rgba(108, 92, 255, 0.7)' }} />
                  ))}

                  {crop.vSplits.map((sx, i) => (
                    <div
                      key={`vh-${i}`}
                      onPointerDown={onPointerDown({ kind: 'vSplit', index: i })}
                      style={{
                        position: 'absolute',
                        left: `calc(${sx * 100}% - 6px)`,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 12, height: 36,
                        background: 'white',
                        border: '2px solid #6C5CFF',
                        borderRadius: 4,
                        cursor: 'ew-resize',
                        boxShadow: '0 0 4px rgba(0,0,0,0.4)',
                        touchAction: 'none',
                      }}
                    />
                  ))}
                  {crop.hSplits.map((sy, i) => (
                    <div
                      key={`hh-${i}`}
                      onPointerDown={onPointerDown({ kind: 'hSplit', index: i })}
                      style={{
                        position: 'absolute',
                        top: `calc(${sy * 100}% - 6px)`,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        height: 12, width: 36,
                        background: 'white',
                        border: '2px solid #6C5CFF',
                        borderRadius: 4,
                        cursor: 'ns-resize',
                        boxShadow: '0 0 4px rgba(0,0,0,0.4)',
                        touchAction: 'none',
                      }}
                    />
                  ))}

                  <FrameHandle pos={{ left: -7, top: -7 }} cursor="nwse-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 'tl' })} />
                  <FrameHandle pos={{ right: -7, top: -7 }} cursor="nesw-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 'tr' })} />
                  <FrameHandle pos={{ left: -7, bottom: -7 }} cursor="nesw-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 'bl' })} />
                  <FrameHandle pos={{ right: -7, bottom: -7 }} cursor="nwse-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 'br' })} />
                  <FrameHandle pos={{ left: -7, top: '50%', transform: 'translateY(-50%)' }} cursor="ew-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 'l' })} />
                  <FrameHandle pos={{ right: -7, top: '50%', transform: 'translateY(-50%)' }} cursor="ew-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 'r' })} />
                  <FrameHandle pos={{ top: -7, left: '50%', transform: 'translateX(-50%)' }} cursor="ns-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 't' })} />
                  <FrameHandle pos={{ bottom: -7, left: '50%', transform: 'translateX(-50%)' }} cursor="ns-resize" onPointerDown={onPointerDown({ kind: 'frame', handle: 'b' })} />
                </div>
              </div>

              <div className="rounded-md border border-slate-200 bg-white p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-medium text-slate-700">
                    {enabledCount} / {tileCount} sticker sẽ được tải lên
                  </p>
                  <p className="text-xs text-slate-500">
                    {bgMode === 'manual' ? 'Bấm vào ô để mở trình vẽ mask. Shift+Click để bỏ qua.' : 'Bấm vào ô để bỏ qua / chọn lại.'}
                  </p>
                </div>
                <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                  {Array.from({ length: tileCount }).map((_, idx) => {
                    const isSkipped = skippedTiles.has(idx);
                    const url = thumbnails[idx];
                    const tileProgress = progress.find((p) => p.index === idx);
                    const hasMask = manualMasks.has(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={(e) => {
                          if (e.shiftKey || bgMode !== 'manual') {
                            setSkippedTiles((prev) => {
                              const next = new Set(prev);
                              if (next.has(idx)) next.delete(idx);
                              else next.add(idx);
                              return next;
                            });
                          } else {
                            setEditingTile(idx);
                          }
                        }}
                        className={`relative aspect-square overflow-hidden rounded-md border-2 ${
                          isSkipped
                            ? 'border-slate-200 opacity-30'
                            : hasMask && bgMode === 'manual'
                              ? 'border-emerald-400'
                              : 'border-slate-300 hover:border-slate-500'
                        }`}
                        style={{
                          backgroundImage: 'repeating-conic-gradient(#e2e8f0 0 25%, #f8fafc 0 50%)',
                          backgroundSize: '12px 12px',
                        }}
                        title={isSkipped ? 'Đã bỏ qua' : `Sticker #${idx + 1}`}
                      >
                        {url ? <img src={url} alt={`Sticker ${idx + 1}`} className="h-full w-full object-contain" /> : null}
                        {tileProgress ? (
                          <span className={`absolute bottom-0 left-0 right-0 h-1 ${
                            tileProgress.status === 'done' ? 'bg-emerald-500'
                              : tileProgress.status === 'error' ? 'bg-red-500'
                              : tileProgress.status === 'uploading' ? 'bg-blue-500'
                              : tileProgress.status === 'hashing' ? 'bg-amber-400'
                              : tileProgress.status === 'bg-removing' ? 'bg-purple-500'
                              : 'bg-transparent'
                          }`} />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setImageURL(null);
                    setImageElement(null);
                    setImageDims(null);
                    setProgress([]);
                    setSkippedTiles(new Set());
                    setManualMasks(new Map());
                  }}
                  disabled={isProcessing}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Chọn ảnh khác
                </button>
                <button
                  type="button"
                  onClick={() => { void handleUpload(); }}
                  disabled={isProcessing || enabledCount === 0}
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {isProcessing ? 'Đang tải lên…' : `Cắt & Tải lên ${enabledCount} sticker`}
                </button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>

      {editingTile !== null && imageElement && imageDims ? (
        <ManualMaskEditor
          tileIndex={editingTile}
          tileRect={tileRects[editingTile]!}
          imageElement={imageElement}
          imageDims={imageDims}
          initialMask={manualMasks.get(editingTile) ?? null}
          onClose={() => setEditingTile(null)}
          onSave={(mask) => {
            setManualMasks((prev) => {
              const next = new Map(prev);
              next.set(editingTile, mask);
              return next;
            });
            setEditingTile(null);
          }}
        />
      ) : null}
    </Dialog.Root>
  );
}

// ---- Manual mask editor --------------------------------------------------

interface ManualMaskEditorProps {
  tileIndex: number;
  tileRect: { x: number; y: number; width: number; height: number; index: number };
  imageElement: HTMLImageElement;
  imageDims: { w: number; h: number };
  initialMask: ImageData | null;
  onClose: () => void;
  onSave: (mask: ImageData) => void;
}

function ManualMaskEditor({
  tileIndex,
  tileRect,
  imageElement,
  imageDims,
  initialMask,
  onClose,
  onSave,
}: ManualMaskEditorProps): JSX.Element {
  const baseCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<'brush' | 'eraser'>('brush');
  const [brushSize, setBrushSize] = useState(40);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Render the source tile to the base canvas exactly once.
  useEffect(() => {
    const canvas = baseCanvasRef.current;
    if (!canvas) return;
    canvas.width = MASK_CANVAS_SIZE;
    canvas.height = MASK_CANVAS_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, MASK_CANVAS_SIZE, MASK_CANVAS_SIZE);
    const { srcX, srcY, srcW, srcH, drawW, drawH, offsetX, offsetY } =
      tilePlacement(tileRect, imageDims, MASK_CANVAS_SIZE);
    ctx.drawImage(imageElement, srcX, srcY, srcW, srcH, offsetX, offsetY, drawW, drawH);
  }, [tileRect, imageElement, imageDims]);

  // Init mask canvas with the saved mask if any. Mask uses
  // black=keep, transparent=remove. We render it as a red overlay.
  useEffect(() => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    canvas.width = MASK_CANVAS_SIZE;
    canvas.height = MASK_CANVAS_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (initialMask) {
      ctx.putImageData(initialMask, 0, 0);
    } else {
      // Default mask = fully opaque (every pixel kept). Admin can erase.
      ctx.fillStyle = 'rgba(108, 92, 255, 0.45)';
      ctx.fillRect(0, 0, MASK_CANVAS_SIZE, MASK_CANVAS_SIZE);
    }
  }, [initialMask]);

  const getCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = maskCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * MASK_CANVAS_SIZE,
      y: ((e.clientY - rect.top) / rect.height) * MASK_CANVAS_SIZE,
    };
  };

  const drawSegment = (from: { x: number; y: number }, to: { x: number; y: number }): void => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (tool === 'brush') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = 'rgba(108, 92, 255, 0.45)';
    } else {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    }
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const p = getCanvasPoint(e);
    lastPointRef.current = p;
    drawSegment(p, p);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawingRef.current) return;
    const p = getCanvasPoint(e);
    const last = lastPointRef.current;
    if (last) drawSegment(last, p);
    lastPointRef.current = p;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const handleSave = (): void => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const data = ctx.getImageData(0, 0, MASK_CANVAS_SIZE, MASK_CANVAS_SIZE);
    onSave(data);
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-slate-900/80" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[70] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg border border-slate-200 bg-white p-6 shadow-xl focus:outline-none">
          <header className="mb-3 flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold text-slate-900">
              Vẽ mask cho sticker #{tileIndex + 1}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Đóng"
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className="mb-3 flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => setTool('brush')}
              className={`rounded-md border px-3 py-1.5 font-medium ${tool === 'brush' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}
            >
              Cọ (giữ)
            </button>
            <button
              type="button"
              onClick={() => setTool('eraser')}
              className={`rounded-md border px-3 py-1.5 font-medium ${tool === 'eraser' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}
            >
              Tẩy (bỏ)
            </button>
            <label className="ml-2 flex items-center gap-2 text-slate-600">
              Kích thước
              <input type="range" min={6} max={150} value={brushSize} onChange={(e) => setBrushSize(Number(e.target.value))} />
              <span className="w-8 text-right tabular-nums">{brushSize}</span>
            </label>
            <button
              type="button"
              onClick={() => {
                const canvas = maskCanvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                ctx.clearRect(0, 0, MASK_CANVAS_SIZE, MASK_CANVAS_SIZE);
                ctx.fillStyle = 'rgba(108, 92, 255, 0.45)';
                ctx.fillRect(0, 0, MASK_CANVAS_SIZE, MASK_CANVAS_SIZE);
              }}
              className="ml-auto rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Xoá hết mask
            </button>
            <button
              type="button"
              onClick={() => {
                const canvas = maskCanvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                ctx.clearRect(0, 0, MASK_CANVAS_SIZE, MASK_CANVAS_SIZE);
              }}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
            >
              Tẩy hết
            </button>
          </div>

          <div
            className="relative mx-auto"
            style={{
              aspectRatio: '1 / 1',
              width: '100%',
              maxWidth: 480,
              backgroundImage: 'repeating-conic-gradient(#e2e8f0 0 25%, #f8fafc 0 50%)',
              backgroundSize: '16px 16px',
            }}
          >
            <canvas ref={baseCanvasRef} className="absolute inset-0 h-full w-full" />
            <canvas
              ref={maskCanvasRef}
              className="absolute inset-0 h-full w-full"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              style={{ touchAction: 'none', cursor: tool === 'brush' ? 'crosshair' : 'cell' }}
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            >
              Huỷ
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800"
            >
              Lưu mask
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---- helpers --------------------------------------------------------

function uniformSplits(n: number): number[] {
  return Array.from({ length: n - 1 }, (_, i) => (i + 1) / n);
}

function buildInitialCrop(rows: number, cols: number): CropState {
  return {
    outer: { x: 0.02, y: 0.02, width: 0.96, height: 0.96 },
    vSplits: uniformSplits(cols),
    hSplits: uniformSplits(rows),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

function tilePlacement(
  tile: { x: number; y: number; width: number; height: number },
  dims: { w: number; h: number },
  outputSize: number,
): { srcX: number; srcY: number; srcW: number; srcH: number; drawW: number; drawH: number; offsetX: number; offsetY: number } {
  const srcX = tile.x * dims.w;
  const srcY = tile.y * dims.h;
  const srcW = tile.width * dims.w;
  const srcH = tile.height * dims.h;
  const scale = Math.min(outputSize / srcW, outputSize / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const offsetX = (outputSize - drawW) / 2;
  const offsetY = (outputSize - drawH) / 2;
  return { srcX, srcY, srcW, srcH, drawW, drawH, offsetX, offsetY };
}

interface DropZoneProps {
  onFile: (file: File) => void;
  onPick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}
function DropZone({ onFile, onPick, fileInputRef }: DropZoneProps): JSX.Element {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onPick();
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
      }}
      className="mt-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center hover:border-slate-400"
    >
      <p className="text-sm font-medium text-slate-700">Kéo thả ảnh vào đây hoặc nhấn để chọn</p>
      <p className="mt-1 text-xs text-slate-500">PNG / JPEG / WebP — bao nhiêu sticker trong ảnh cũng được, bạn quyết.</p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/webp,image/jpeg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

interface NumberStepperProps {
  label: string;
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
}
function NumberStepper({ label, value, onChange, min, max }: NumberStepperProps): JSX.Element {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      <button type="button" onClick={dec} className="h-7 w-7 rounded-md border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:bg-slate-100">−</button>
      <span className="w-7 text-center text-sm font-semibold tabular-nums text-slate-900">{value}</span>
      <button type="button" onClick={inc} className="h-7 w-7 rounded-md border border-slate-300 bg-white text-sm font-bold text-slate-700 hover:bg-slate-100">+</button>
    </div>
  );
}

interface BgModeButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}
function BgModeButton({ active, onClick, children }: BgModeButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}
    >
      {children}
    </button>
  );
}

interface FrameHandleProps {
  pos: React.CSSProperties;
  cursor: string;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
}
function FrameHandle({ pos, cursor, onPointerDown }: FrameHandleProps): JSX.Element {
  return (
    <div
      onPointerDown={onPointerDown}
      style={{
        position: 'absolute',
        width: 14, height: 14,
        background: 'white',
        border: '2px solid #6C5CFF',
        borderRadius: 999,
        boxShadow: '0 0 4px rgba(0,0,0,0.4)',
        cursor,
        touchAction: 'none',
        ...pos,
      }}
    />
  );
}

// ---- canvas operations ----------------------------------------------

interface TileRect { x: number; y: number; width: number; height: number; index: number; }
interface CancelToken { cancelled: boolean; }

async function renderThumbnails(
  img: HTMLImageElement,
  dims: { w: number; h: number },
  tiles: TileRect[],
  bgMode: BgMode,
  manualMasks: Map<number, ImageData>,
  cancelToken: CancelToken,
  setAiPreviewProgress: (p: { index: number; total: number } | null) => void,
): Promise<string[]> {
  // No AI in the thumbnail loop — it ran once per tile on every state
  // change and froze the UI for tens of seconds. Thumbnails always show
  // the raw cropped tile (plus the manual mask if set); AI bg-removal
  // happens only at upload time.
  void setAiPreviewProgress;
  void bgMode; // explicitly unused here

  const SIZE = 128;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];

  const urls: string[] = [];
  for (const t of tiles) {
    if (cancelToken.cancelled) return urls;
    ctx.clearRect(0, 0, SIZE, SIZE);
    const p = tilePlacement(t, dims, SIZE);
    ctx.drawImage(img, p.srcX, p.srcY, p.srcW, p.srcH, p.offsetX, p.offsetY, p.drawW, p.drawH);

    const mask = manualMasks.get(t.index);
    if (mask) {
      applyManualMask(ctx, SIZE, mask);
    }
    urls.push(canvas.toDataURL('image/png'));
  }
  return urls;
}

/**
 * Apply a manual mask to the destination canvas. The mask is stored at
 * MASK_CANVAS_SIZE; we resample down to the output size by using a
 * temporary canvas + compositing operations:
 *
 *   1. Render the mask onto a temp canvas at the target size.
 *   2. Use `destination-in` so only pixels under the mask survive.
 */
function applyManualMask(ctx: CanvasRenderingContext2D, size: number, mask: ImageData): void {
  const tmp = document.createElement('canvas');
  tmp.width = MASK_CANVAS_SIZE;
  tmp.height = MASK_CANVAS_SIZE;
  const tmpCtx = tmp.getContext('2d');
  if (!tmpCtx) return;
  tmpCtx.putImageData(mask, 0, 0);

  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(tmp, 0, 0, MASK_CANVAS_SIZE, MASK_CANVAS_SIZE, 0, 0, size, size);
  ctx.restore();
}
